import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  TextField,
  CircularProgress,
  InputAdornment,
  IconButton,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ClearIcon from '@mui/icons-material/Clear';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import { useLeadDetails } from '../../contexts/LeadDetailsContext';

const T = {
  green: '#0a6b3e',
  greenLt: '#e5f5ec',
  greenMd: '#0d8a50',
  greenSf: 'rgba(10,107,62,0.06)',
  ink: '#0f1a13',
  subtle: '#526b5c',
  muted: '#8eaa98',
  bg: '#f5f8f6',
  border: '#dce8e1',
  borderL: '#eaf1ec',
  white: '#ffffff',
  red: '#dc2626',
  redLt: '#fef2f2',
  redBorder: '#fecaca',
  font: "'Inter','DM Sans','Plus Jakarta Sans',sans-serif",
};

const chipSelectedSx = {
  fontFamily: T.font,
  m: 0.5,
  height: 28,
  borderRadius: 1.5,
  bgcolor: T.greenLt,
  color: T.green,
  border: `1px solid ${T.border}`,
  fontSize: 12,
  fontWeight: 500,
  '& .MuiChip-label': { px: 1.25 },
  '& .MuiChip-deleteIcon': { color: T.subtle, '&:hover': { color: T.green } },
};

const suggestedChipSx = {
  fontFamily: T.font,
  fontSize: 12,
  height: 26,
  borderRadius: 1.5,
  bgcolor: T.bg,
  color: T.subtle,
  border: `1px solid ${T.borderL}`,
  '&:hover': {
    bgcolor: T.greenSf,
    borderColor: T.border,
    color: T.green,
    cursor: 'pointer',
  },
};

const inputBoxSx = {
  border: `1px solid ${T.borderL}`,
  borderRadius: 2,
  p: 1,
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  bgcolor: T.white,
  transition: 'border-color 0.15s',
  '&:focus-within': {
    borderColor: T.green,
    boxShadow: `0 0 0 1px ${T.green}22`,
  },
};

const inlineFieldSx = {
  flex: '1 1 auto',
  m: 0.5,
  minWidth: 180,
  '& .MuiOutlinedInput-root': {
    fontFamily: T.font,
    fontSize: 13,
    borderRadius: 1,
    bgcolor: 'transparent',
    '& fieldset': { border: 'none' },
  },
  '& .MuiOutlinedInput-input::placeholder': {
    fontFamily: T.font,
    opacity: 0.65,
  },
};

const InterestsUpdate = ({ open, onClose, leadId }) => {
  const { preferences, fetchLeadPreference, updateLeadPreferences } = useLeadDetails();
  const [loading, setLoading] = useState(false);
  const [selectedInterests, setSelectedInterests] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [saveError, setSaveError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setSaveError(null);
    if (open && preferences?.saved_preferences) {
      setSelectedInterests(preferences.saved_preferences.interests || []);
    }
  }, [open, preferences]);

  const titleCaseInterest = (interest) =>
    interest
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');

  const handleSave = async () => {
    setLoading(true);
    setSaveError(null);
    try {
      const titlecaseInterests = selectedInterests.map(titleCaseInterest);
      await updateLeadPreferences(leadId, { interest: titlecaseInterests }, 'additional_preferences');
      await fetchLeadPreference(leadId);
      onClose();
    } catch (err) {
      setSaveError(err?.message || 'Could not save interests. Try again.');
    } finally {
      setLoading(false);
    }
  };

  const hasInterest = (candidate) =>
    selectedInterests.some((i) => i.toLowerCase() === candidate.trim().toLowerCase());

  const handleAddInterest = () => {
    const next = inputValue.trim();
    if (next && !hasInterest(next)) {
      setSelectedInterests((prev) => [...prev, next]);
    }
    setInputValue('');
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleAddInterest();
    }
  };

  const handleRemoveInterest = (interestToRemove) => {
    setSelectedInterests((prev) => prev.filter((interest) => interest !== interestToRemove));
  };

  const suggestedInterests = [
    'Residential',
    'Commercial',
    'Investment',
    'Ready To Move',
    'Near Metro',
    'Gated Community',
    'Luxury',
    'Affordable',
    'Family-Friendly',
    'Rental Property',
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          maxWidth: 480,
          width: '100%',
          borderRadius: 3,
          fontFamily: T.font,
          overflow: 'hidden',
          m: 2,
        },
      }}
    >
      <DialogTitle sx={{ p: 0 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2.5,
            py: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '10px',
                bgcolor: T.greenLt,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <LightbulbOutlinedIcon sx={{ fontSize: 22, color: T.green }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontFamily: T.font, fontSize: 16, fontWeight: 700, color: T.ink, lineHeight: 1.3 }}>
                Interests
              </Typography>
              <Typography sx={{ fontFamily: T.font, fontSize: 11.5, color: T.muted, mt: 0.25, lineHeight: 1.45 }}>
                Add or remove interests to describe what this lead is looking for
              </Typography>
            </Box>
          </Box>
          <IconButton
            size="small"
            onClick={onClose}
            aria-label="Close"
            disabled={loading}
            sx={{ color: T.muted, '&:hover': { color: T.ink } }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <Divider sx={{ borderColor: T.borderL }} />

      <DialogContent sx={{ px: 2.5, pt: 2.5, pb: 1 }}>
        <Typography sx={{ fontFamily: T.font, fontSize: 11, fontWeight: 700, color: T.subtle, textTransform: 'uppercase', letterSpacing: '0.04em', mb: 1 }}>
          Current interests
        </Typography>

        <Box sx={{ ...inputBoxSx, mb: 2 }}>
          {selectedInterests.map((interest, index) => (
            <Chip
              key={`${interest}-${index}`}
              label={interest}
              onDelete={() => handleRemoveInterest(interest)}
              deleteIcon={<CloseIcon sx={{ fontSize: 16 }} />}
              sx={chipSelectedSx}
            />
          ))}
          <TextField
            placeholder="Type and press Enter to add"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            variant="outlined"
            size="small"
            InputProps={{
              endAdornment: inputValue ? (
                <InputAdornment position="end">
                  <IconButton onClick={() => setInputValue('')} edge="end" size="small" sx={{ color: T.muted }}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
            sx={inlineFieldSx}
          />
        </Box>

        <Typography sx={{ fontFamily: T.font, fontSize: 11, fontWeight: 700, color: T.subtle, textTransform: 'uppercase', letterSpacing: '0.04em', mb: 1 }}>
          Suggested
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1 }}>
          {suggestedInterests
            .filter((interest) => !hasInterest(interest))
            .map((interest) => (
              <Chip
                key={interest}
                label={interest}
                size="small"
                onClick={() => {
                  if (!hasInterest(interest)) {
                    setSelectedInterests((prev) => [...prev, interest]);
                  }
                }}
                sx={suggestedChipSx}
              />
            ))}
        </Box>

        {saveError && (
          <Box
            sx={{
              mt: 2,
              p: 1.5,
              borderRadius: 2,
              border: `1px solid ${T.redBorder}`,
              bgcolor: T.redLt,
            }}
          >
            <Typography sx={{ fontFamily: T.font, fontSize: 12.5, color: T.red }}>{saveError}</Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 2.5,
          py: 2,
          gap: 1,
          justifyContent: 'flex-end',
          borderTop: `1px solid ${T.borderL}`,
          bgcolor: T.bg,
        }}
      >
        <Button
          onClick={onClose}
          disabled={loading}
          sx={{
            fontFamily: T.font,
            fontSize: 13,
            fontWeight: 500,
            color: T.subtle,
            textTransform: 'none',
            borderRadius: 2,
            px: 2,
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={loading}
          sx={{
            fontFamily: T.font,
            fontSize: 13,
            fontWeight: 600,
            textTransform: 'none',
            borderRadius: 2,
            px: 2.5,
            minWidth: 132,
            bgcolor: T.green,
            color: '#fff',
            boxShadow: 'none',
            '&:hover': { bgcolor: T.greenMd, boxShadow: 'none' },
            '&.Mui-disabled': { bgcolor: T.borderL, color: T.muted },
          }}
        >
          {loading ? <CircularProgress size={22} thickness={4} sx={{ color: '#fff' }} /> : 'Save changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InterestsUpdate;
